
import {
  defineComponent, computed, ref, nextTick,
} from 'vue';
import Icone from '@/core/components/Icone.vue';
import { IColumn } from '@/core/models/AntDesign/IColumn';
import { IOrdenacao } from '@/core/models/Consulta/IOrdenacao';

export default defineComponent({
  name: 'ColunaGrade',
  props: {
    column: {
      type: Object as () => IColumn,
      required: true,
    },
    ordenacaoSelecionada: {
      type: Array as () => IOrdenacao[],
      required: true,
    },
    filtrosCabecalho: {
      type: Object as () => Record<string, string>,
      required: false,
      default: undefined,
    },
  },
  components: {
    Icone,
  },
  emits: ['update:ordenacaoSelecionada', 'update:filtrosCabecalho', 'buscarDados'],
  setup(props, { emit }) {
    let debounceFiltroCabecalho = 0;
    const filtroCabecalhoAberto = ref(false);
    const inputFiltroCabecalho = ref<HTMLInputElement | null>(null);

    const computedOrdenacaoSelecionada = computed({
      get: () => props.ordenacaoSelecionada,
      set: (objeto: IOrdenacao[]) => {
        emit('update:ordenacaoSelecionada', objeto);
      },
    });

    const computedFiltrosCabecalho = computed({
      get: () => props.filtrosCabecalho,
      set: (objeto: Record<string, string> | undefined) => {
        emit('update:filtrosCabecalho', objeto);
      },
    });

    function ordenacaoAtiva(key: string, sort: string): boolean {
      if (computedOrdenacaoSelecionada.value.length > 0) {
        const itemOrdem = computedOrdenacaoSelecionada.value.find(((coluna) => coluna.identificador === key));
        if (itemOrdem === undefined) {
          return false;
        }
        if (itemOrdem.identificador === key && itemOrdem.ordem === sort) {
          return true;
        }
      }
      return false;
    }

    function obterIdentificadorCabecalho(): string {
      return props.column.identificadorConsulta ?? props.column.key;
    }

    function preencheOrdenacaoSelecionada(key: string, sort: string) {
      if (computedOrdenacaoSelecionada.value.length > 0) {
        const itemOrdem = computedOrdenacaoSelecionada.value.find(((coluna) => coluna.identificador === key));
        if (itemOrdem === undefined) {
          computedOrdenacaoSelecionada.value.push({ identificador: key, ordem: sort } as IOrdenacao);
        } else if (itemOrdem.ordem === sort) {
          const indice = computedOrdenacaoSelecionada.value.indexOf(itemOrdem);
          computedOrdenacaoSelecionada.value.splice(indice, 1);
        } else {
          itemOrdem.ordem = sort;
        }
      } else {
        computedOrdenacaoSelecionada.value.push({ identificador: key, ordem: sort } as IOrdenacao);
      }
    }

    function ordenarDados(key: string, sort: string) {
      preencheOrdenacaoSelecionada(key, sort);
      emit('buscarDados');
    }

    const exibirFiltroCabecalho = computed(() => {
      if (computedFiltrosCabecalho.value === undefined) {
        return false;
      }
      return props.column.filtering === true;
    });

    function obtemValorFiltroCabecalho(): string {
      if (computedFiltrosCabecalho.value === undefined) {
        return '';
      }
      return computedFiltrosCabecalho.value[obterIdentificadorCabecalho()] ?? '';
    }

    const filtroAtivo = computed(() => obtemValorFiltroCabecalho().trim() !== '');
    const filtroLigado = computed(() => filtroCabecalhoAberto.value || filtroAtivo.value);
    const paddingAcoesCabecalho = computed(() => {
      let espaco = 0;
      if (props.column.ordering === true) {
        espaco += 26;
      }
      if (exibirFiltroCabecalho.value) {
        espaco += 24;
      }
      if (espaco > 0) {
        espaco += 10;
      }
      return `${espaco}px`;
    });

    async function alternarFiltroCabecalho() {
      const textoFiltroAtivo = obtemValorFiltroCabecalho().trim() !== '';

      if (textoFiltroAtivo) {
        if (computedFiltrosCabecalho.value !== undefined) {
          const novosFiltrosCabecalho = {
            ...computedFiltrosCabecalho.value,
          };
          delete novosFiltrosCabecalho[obterIdentificadorCabecalho()];
          computedFiltrosCabecalho.value = novosFiltrosCabecalho;
        }
        filtroCabecalhoAberto.value = false;
        emit('buscarDados');
        return;
      }

      filtroCabecalhoAberto.value = !filtroCabecalhoAberto.value;
      if (filtroCabecalhoAberto.value) {
        await nextTick();
        if (inputFiltroCabecalho.value !== null) {
          inputFiltroCabecalho.value.focus();
        }
      }
    }

    function fecharFiltroCabecalho() {
      filtroCabecalhoAberto.value = false;
    }

    function alterarFiltroCabecalho(evento: Event) {
      if (computedFiltrosCabecalho.value === undefined) {
        return;
      }

      const campo = evento.target as HTMLInputElement;
      const novoValor = campo.value;
      const identificadorCabecalho = obterIdentificadorCabecalho();
      const novosFiltrosCabecalho = {
        ...computedFiltrosCabecalho.value,
        [identificadorCabecalho]: novoValor,
      };

      if (novoValor.trim() === '') {
        delete novosFiltrosCabecalho[identificadorCabecalho];
      }

      computedFiltrosCabecalho.value = novosFiltrosCabecalho;

      clearTimeout(debounceFiltroCabecalho);
      debounceFiltroCabecalho = setTimeout(() => {
        emit('buscarDados');
      }, 600);
    }

    return {
      props,
      ordenacaoAtiva,
      ordenarDados,
      obterIdentificadorCabecalho,
      exibirFiltroCabecalho,
      obtemValorFiltroCabecalho,
      filtroCabecalhoAberto,
      inputFiltroCabecalho,
      filtroAtivo,
      filtroLigado,
      paddingAcoesCabecalho,
      alternarFiltroCabecalho,
      fecharFiltroCabecalho,
      alterarFiltroCabecalho,
    };
  },
});
